import {
  type ApiResponse,
  type PaginatedResponse
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  TutoringRate,
  CreateTutoringRateInput,
  UpdateTutoringRateInput,
  TutoringGradePremium,
  CreateTutoringGradePremiumInput,
  UpdateTutoringGradePremiumInput,
  TutoringBulkDiscount,
  CreateTutoringBulkDiscountInput,
  UpdateTutoringBulkDiscountInput,
  RevisionRate,
  CreateRevisionRateInput,
  UpdateRevisionRateInput,
  ApplicationPackage,
  CreateApplicationPackageInput,
  UpdateApplicationPackageInput,
  ApplicationGroup,
  AllPricingData,
  TutoringQuoteParams,
  TutoringQuote
} from './types/pricing.types';

// ── Combined (public - powers the calculator + public pricing pages) ────────

// Public marketing pages (Server Components) call this. 5-minute revalidate:
// dashboard edits show up within 5 minutes with no rebuild/redeploy, while
// staying fast/cacheable rather than fetching on every request.
export async function getAllPricingData(): Promise<ApiResponse<AllPricingData>> {

  return apiRequest<ApiResponse<AllPricingData>>('/pricing/all', {
    next: {
      revalidate: 300
    }
  } as RequestInit);
}

// ── Tutoring Quote (calculator lookup) ───────────────────────────────────────

// Public, uncached - the whole point is a fresh server-side lookup per
// click, returning only the computed total, never the underlying rate
// tables the way the old client-side calculator used to require.
export async function getTutoringQuote(params: TutoringQuoteParams): Promise<ApiResponse<TutoringQuote>> {

  const query = new URLSearchParams();

  query.set('phase', params.phase);
  query.set('curriculum', params.curriculum);
  query.set('deliveryMode', params.deliveryMode);

  if (params.gradeNumber !== undefined) {

    query.set('gradeNumber', String(params.gradeNumber));
  }

  query.set('sessionMinutes', String(params.sessionMinutes));
  query.set('sessionCount', String(params.sessionCount));

  return apiRequest<ApiResponse<TutoringQuote>>(`/pricing/tutoring-quote?${ query.toString() }`);
}

// ── Tutoring Rates ────────────────────────────────────────────────────────────

export interface ListTutoringRatesParams {
  isActive?: boolean
  limit?: number
  offset?: number
}

export async function listTutoringRates(
  params: ListTutoringRatesParams = {},
  token?: string
): Promise<PaginatedResponse<TutoringRate>> {

  const query = new URLSearchParams();

  if (params.isActive !== undefined) {

    query.set('isActive', String(params.isActive));
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<TutoringRate>>(`/pricing/tutoring-rates${ qs }`, undefined, token);
}

export async function createTutoringRate(
  input: CreateTutoringRateInput,
  token: string
): Promise<ApiResponse<TutoringRate>> {

  return apiRequest<ApiResponse<TutoringRate>>(
    '/pricing/tutoring-rates',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateTutoringRate(
  id: number,
  input: UpdateTutoringRateInput,
  token: string
): Promise<ApiResponse<TutoringRate>> {

  return apiRequest<ApiResponse<TutoringRate>>(
    `/pricing/tutoring-rates/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteTutoringRate(id: number, token: string): Promise<ApiResponse<null>> {

  return apiRequest<ApiResponse<null>>(`/pricing/tutoring-rates/${ id }`, {
    method: 'DELETE'
  }, token);
}

// ── Tutoring Grade Premiums ───────────────────────────────────────────────────

export interface ListTutoringGradePremiumsParams {
  isActive?: boolean
  limit?: number
  offset?: number
}

export async function listTutoringGradePremiums(
  params: ListTutoringGradePremiumsParams = {},
  token?: string
): Promise<PaginatedResponse<TutoringGradePremium>> {

  const query = new URLSearchParams();

  if (params.isActive !== undefined) {

    query.set('isActive', String(params.isActive));
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<TutoringGradePremium>>(`/pricing/tutoring-grade-premiums${ qs }`, undefined, token);
}

export async function createTutoringGradePremium(
  input: CreateTutoringGradePremiumInput,
  token: string
): Promise<ApiResponse<TutoringGradePremium>> {

  return apiRequest<ApiResponse<TutoringGradePremium>>(
    '/pricing/tutoring-grade-premiums',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateTutoringGradePremium(
  id: number,
  input: UpdateTutoringGradePremiumInput,
  token: string
): Promise<ApiResponse<TutoringGradePremium>> {

  return apiRequest<ApiResponse<TutoringGradePremium>>(
    `/pricing/tutoring-grade-premiums/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteTutoringGradePremium(id: number, token: string): Promise<ApiResponse<null>> {

  return apiRequest<ApiResponse<null>>(`/pricing/tutoring-grade-premiums/${ id }`, {
    method: 'DELETE'
  }, token);
}

// ── Tutoring Bulk Discounts ────────────────────────────────────────────────────

export interface ListTutoringBulkDiscountsParams {
  isActive?: boolean
  limit?: number
  offset?: number
}

export async function listTutoringBulkDiscounts(
  params: ListTutoringBulkDiscountsParams = {},
  token?: string
): Promise<PaginatedResponse<TutoringBulkDiscount>> {

  const query = new URLSearchParams();

  if (params.isActive !== undefined) {

    query.set('isActive', String(params.isActive));
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<TutoringBulkDiscount>>(`/pricing/tutoring-bulk-discounts${ qs }`, undefined, token);
}

export async function createTutoringBulkDiscount(
  input: CreateTutoringBulkDiscountInput,
  token: string
): Promise<ApiResponse<TutoringBulkDiscount>> {

  return apiRequest<ApiResponse<TutoringBulkDiscount>>(
    '/pricing/tutoring-bulk-discounts',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateTutoringBulkDiscount(
  id: number,
  input: UpdateTutoringBulkDiscountInput,
  token: string
): Promise<ApiResponse<TutoringBulkDiscount>> {

  return apiRequest<ApiResponse<TutoringBulkDiscount>>(
    `/pricing/tutoring-bulk-discounts/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteTutoringBulkDiscount(id: number, token: string): Promise<ApiResponse<null>> {

  return apiRequest<ApiResponse<null>>(`/pricing/tutoring-bulk-discounts/${ id }`, {
    method: 'DELETE'
  }, token);
}

// ── Revision Rates ─────────────────────────────────────────────────────────────

export interface ListRevisionRatesParams {
  isActive?: boolean
  limit?: number
  offset?: number
}

export async function listRevisionRates(
  params: ListRevisionRatesParams = {},
  token?: string
): Promise<PaginatedResponse<RevisionRate>> {

  const query = new URLSearchParams();

  if (params.isActive !== undefined) {

    query.set('isActive', String(params.isActive));
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<RevisionRate>>(`/pricing/revision-rates${ qs }`, undefined, token);
}

export async function createRevisionRate(
  input: CreateRevisionRateInput,
  token: string
): Promise<ApiResponse<RevisionRate>> {

  return apiRequest<ApiResponse<RevisionRate>>(
    '/pricing/revision-rates',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateRevisionRate(
  id: number,
  input: UpdateRevisionRateInput,
  token: string
): Promise<ApiResponse<RevisionRate>> {

  return apiRequest<ApiResponse<RevisionRate>>(
    `/pricing/revision-rates/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteRevisionRate(id: number, token: string): Promise<ApiResponse<null>> {

  return apiRequest<ApiResponse<null>>(`/pricing/revision-rates/${ id }`, {
    method: 'DELETE'
  }, token);
}

// ── Application Packages ────────────────────────────────────────────────────────

export interface ListApplicationPackagesParams {
  groupKey?: ApplicationGroup
  isActive?: boolean
  limit?: number
  offset?: number
}

export async function listApplicationPackages(
  params: ListApplicationPackagesParams = {},
  token?: string
): Promise<PaginatedResponse<ApplicationPackage>> {

  const query = new URLSearchParams();

  if (params.groupKey !== undefined) {

    query.set('groupKey', params.groupKey);
  }

  if (params.isActive !== undefined) {

    query.set('isActive', String(params.isActive));
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<ApplicationPackage>>(`/pricing/application-packages${ qs }`, undefined, token);
}

export async function getApplicationPackageById(id: number, token?: string): Promise<ApiResponse<ApplicationPackage>> {

  return apiRequest<ApiResponse<ApplicationPackage>>(`/pricing/application-packages/${ id }`, undefined, token);
}

export async function createApplicationPackage(
  input: CreateApplicationPackageInput,
  token: string
): Promise<ApiResponse<ApplicationPackage>> {

  return apiRequest<ApiResponse<ApplicationPackage>>(
    '/pricing/application-packages',
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function updateApplicationPackage(
  id: number,
  input: UpdateApplicationPackageInput,
  token: string
): Promise<ApiResponse<ApplicationPackage>> {

  return apiRequest<ApiResponse<ApplicationPackage>>(
    `/pricing/application-packages/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function deleteApplicationPackage(id: number, token: string): Promise<ApiResponse<null>> {

  return apiRequest<ApiResponse<null>>(`/pricing/application-packages/${ id }`, {
    method: 'DELETE'
  }, token);
}
